import { z } from "zod";
import { HistoryOrigin, HistoryOriginSchema } from "@remi/enums/history-origin";
import { TicketCategorySchema } from "@remi/enums/ticket/category";
import { TicketHistoryField } from "@remi/enums/ticket/history/field";
import { TicketPrioritySchema } from "@remi/enums/ticket/priority";
import { TicketStatusSchema } from "@remi/enums/ticket/status";
import { TicketSubcategorySchema } from "@remi/enums/ticket/subcategory";

export const TicketHistorySchema = z
  .object({
    id: z.string(),
    ticketId: z.string(),
    changedById: z.string(),
    changeGroupId: z.string(),
    reason: z.string().nullish(),
    createdAt: z.coerce.date(),
    origin: HistoryOriginSchema.catch(HistoryOrigin.INITIAL_UNKNOWN),
  })
  .and(
    z.discriminatedUnion("field", [
      z.object({
        field: z.literal(TicketHistoryField.deletedAt),
        changedTo: z.coerce.date().nullable(),
        changedFrom: z.coerce.date().nullable(),
      }),
      z.object({
        field: z.literal(TicketHistoryField.title),
        changedTo: z.string().nullable(),
        changedFrom: z.string().nullable(),
      }),
      z.object({
        field: z.literal(TicketHistoryField.description),
        changedTo: z.string().nullable(),
        changedFrom: z.string().nullable(),
      }),
      z.object({
        field: z.literal(TicketHistoryField.status),
        changedTo: TicketStatusSchema.nullable().catch(null),
        changedFrom: TicketStatusSchema.nullable().catch(null),
      }),
      z.object({
        field: z.literal(TicketHistoryField.priority),
        changedTo: TicketPrioritySchema.nullable().catch(null),
        changedFrom: TicketPrioritySchema.nullable().catch(null),
      }),
      z.object({
        field: z.literal(TicketHistoryField.projectId),
        changedTo: z.coerce.number().nullable(),
        changedFrom: z.coerce.number().nullable(),
      }),
      z.object({
        field: z.literal(TicketHistoryField.assigneeId),
        changedTo: z.string().nullable(),
        changedFrom: z.string().nullable(),
      }),
      z.object({
        field: z.literal(TicketHistoryField.teamId),
        changedTo: z.string().nullable(),
        changedFrom: z.string().nullable(),
      }),
      z.object({
        field: z.literal(TicketHistoryField.followUpDate),
        changedTo: z.coerce.date().nullable(),
        changedFrom: z.coerce.date().nullable(),
      }),
      z.object({
        field: z.literal(TicketHistoryField.resolutionNotes),
        changedTo: z.string().nullable(),
        changedFrom: z.string().nullable(),
      }),
      z.object({
        field: z.literal(TicketHistoryField.category),
        changedTo: TicketCategorySchema.nullable().catch(null),
        changedFrom: TicketCategorySchema.nullable().catch(null),
      }),
      z.object({
        field: z.literal(TicketHistoryField.subcategory),
        changedTo: TicketSubcategorySchema.nullable().catch(null),
        changedFrom: TicketSubcategorySchema.nullable().catch(null),
      }),
      z.object({
        field: z.literal(TicketHistoryField.tags),
        changedTo: z.string().nullable(),
        changedFrom: z.string().nullable(),
      }),
    ]),
  );

export type TicketHistory = z.infer<typeof TicketHistorySchema>;
